import { useState } from 'react';
import { Building2, Plus, Users, FileText, Search, Key, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { useCustomers, useCreateCustomer, useLicenses, useCreateLicense, useProducts } from '../../hooks/useAdminData';
import { usePermissions } from '../../hooks/usePermissions';
import type { Customer } from '../../types';

export function CustomersAdmin() {
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [expandedCustomerId, setExpandedCustomerId] = useState<string | null>(null);

  const { canManageAllCustomers } = usePermissions();
  const { data: customersData, isLoading } = useCustomers(search);
  const { data: licensesData, refetch: refetchLicenses } = useLicenses();
  const { data: productsData } = useProducts();
  const createCustomer = useCreateCustomer();
  const createLicense = useCreateLicense();

  const customers = customersData?.customers ?? [];
  const licenses = licensesData?.licenses ?? [];
  const products = productsData?.products ?? [];

  const handleCreateCustomer = async () => {
    if (!newCustomerName.trim()) return;
    try {
      await createCustomer.mutateAsync(newCustomerName);
      setNewCustomerName('');
      setShowCreateModal(false);
    } catch (error) {
      console.error('Failed to create customer:', error);
    }
  };

  const handleOpenLicenseModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setSelectedProductId('');
    setShowLicenseModal(true);
  };

  const handleAddLicense = async () => {
    if (!selectedCustomer || !selectedProductId) return;
    try {
      await createLicense.mutateAsync({
        customerId: selectedCustomer.id,
        productId: selectedProductId,
      });
      setShowLicenseModal(false);
      setSelectedCustomer(null);
      setSelectedProductId('');
      refetchLicenses();
    } catch (error) {
      console.error('Failed to create license:', error);
    }
  };

  const getCustomerLicenses = (customerId: string) => {
    return licenses.filter(l => l.customer?.id === customerId);
  };

  const getLicenseCount = (customerId: string) => {
    return licenses.filter(l => l.customer?.id === customerId).length;
  };

  const productUrls: Record<string, string> = {
    'LCL': 'https://contractlens.lynki.no',
    'CONTRACTLENS': 'https://contractlens.lynki.no',
  };

  const toggleExpand = (customerId: string) => {
    setExpandedCustomerId(prev => prev === customerId ? null : customerId);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Customers</h1>
          <p className="text-slate-600 mt-1">Manage customer organizations and their licenses</p>
        </div>
        {canManageAllCustomers && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            Add Customer
          </button>
        )}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search customers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Customers List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        {isLoading ? (
          <div className="p-8 text-center text-slate-500">Loading customers...</div>
        ) : customers.length === 0 ? (
          <div className="p-8 text-center text-slate-500">No customers found</div>
        ) : (
          <div className="divide-y divide-slate-200">
            {customers.map((customer) => {
              const isExpanded = expandedCustomerId === customer.id;
              const customerLicenses = getCustomerLicenses(customer.id);
              return (
                <div key={customer.id}>
                  <div
                    className="p-4 flex items-center justify-between hover:bg-slate-50 cursor-pointer"
                    onClick={() => toggleExpand(customer.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Building2 size={24} className="text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                          <h3 className="font-medium text-blue-700 hover:text-blue-900">
                            {customer.customerNumber ? <span className="text-slate-400 mr-1.5">#{customer.customerNumber}</span> : null}
                            {customer.name}
                          </h3>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500 ml-6">
                          <span className="flex items-center gap-1">
                            <FileText size={14} />
                            {getLicenseCount(customer.id)} lisenser
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleOpenLicenseModal(customer)}
                        className="flex items-center gap-1 text-sm text-green-600 hover:text-green-800 px-3 py-1.5 rounded-lg hover:bg-green-50"
                      >
                        <Key size={16} />
                        Lisenser
                      </button>
                      <a
                        href={`/admin/users?customerId=${customer.id}`}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-lg hover:bg-blue-50"
                      >
                        <Users size={16} />
                        Brukere
                      </a>
                    </div>
                  </div>

                  {/* Expanded detail view */}
                  {isExpanded && (
                    <div className="px-4 pb-4 bg-slate-50 border-t border-slate-100">
                      <div className="ml-16 pt-4">
                        {/* Products / Licenses */}
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-slate-700 mb-2">Produkter og lisenser</h4>
                          {customerLicenses.length === 0 ? (
                            <p className="text-sm text-slate-500">Ingen lisenser tildelt</p>
                          ) : (
                            <div className="space-y-2">
                              {customerLicenses.map((license) => {
                                const productCode = license.product?.code?.toUpperCase() || license.product?.name?.toUpperCase().replace(/\s+/g, '') || '';
                                const baseUrl = productUrls[productCode] || productUrls['CONTRACTLENS'];
                                const productUrl = baseUrl ? `${baseUrl}?cid=${btoa(customer.id)}` : null;
                                return (
                                  <div
                                    key={license.id}
                                    className="flex items-center justify-between bg-white px-4 py-3 rounded-lg border border-slate-200"
                                  >
                                    <div>
                                      <span className="text-sm font-medium text-slate-800">{license.product?.name || license.name}</span>
                                      <span className="text-xs text-slate-500 ml-2">({license.name})</span>
                                    </div>
                                    {productUrl && (
                                      <a
                                        href={productUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-lg hover:bg-blue-50"
                                      >
                                        <ExternalLink size={14} />
                                        Gå til program
                                      </a>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Quick actions */}
                        <div className="flex items-center gap-3 pt-2 border-t border-slate-200">
                          <a
                            href={`/admin/users?customerId=${customer.id}`}
                            className="flex items-center gap-1 text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Users size={16} />
                            Administrer brukere
                          </a>
                          <button
                            onClick={() => handleOpenLicenseModal(customer)}
                            className="flex items-center gap-1 text-sm bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <Key size={16} />
                            Administrer lisenser
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Customer Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Create Customer</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Customer Name
              </label>
              <input
                type="text"
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
                placeholder="Enter customer name"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCustomer}
                disabled={createCustomer.isPending || !newCustomerName.trim()}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {createCustomer.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* License Management Modal */}
      {showLicenseModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold text-slate-800 mb-2">
              Licenses for {selectedCustomer.name}
            </h2>
            <p className="text-slate-600 text-sm mb-4">Manage product licenses for this customer</p>

            {/* Existing Licenses */}
            <div className="mb-4">
              <h3 className="text-sm font-medium text-slate-700 mb-2">Current Licenses</h3>
              {getCustomerLicenses(selectedCustomer.id).length === 0 ? (
                <p className="text-slate-500 text-sm py-2">No licenses assigned</p>
              ) : (
                <ul className="space-y-2">
                  {getCustomerLicenses(selectedCustomer.id).map((license) => (
                    <li
                      key={license.id}
                      className="flex items-center justify-between bg-slate-50 px-3 py-2 rounded-lg"
                    >
                      <span className="text-sm text-slate-700">{license.name}</span>
                      <span className="text-xs text-slate-500">{license.product?.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Add New License */}
            <div className="border-t border-slate-200 pt-4 mb-4">
              <h3 className="text-sm font-medium text-slate-700 mb-2">Add New License</h3>
              <select
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="">Select a product...</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowLicenseModal(false);
                  setSelectedCustomer(null);
                }}
                className="px-4 py-2 text-slate-600 hover:text-slate-800"
              >
                Close
              </button>
              <button
                onClick={handleAddLicense}
                disabled={createLicense.isPending || !selectedProductId}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {createLicense.isPending ? 'Adding...' : 'Add License'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomersAdmin;
